import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Eye } from "lucide-react";
import { Menu, MenuItem, Checkbox, CircularProgress } from "@mui/material";
import { NavLink } from "react-router-dom";
import {
  infoToast,
  successToast,
  errorToast,
} from "../../../../../services/toast.service";
import "../../styles/OrderStyles.css";
import { dummyOrders } from "../../../../../dummyData";

const defaultColumns = [
  { id: "orderId", label: "Order ID", minWidth: "120px", required: true },
  { id: "firmName", label: "Firm Name", minWidth: "160px", required: true },
  { id: "date", label: "Date", minWidth: "120px", required: true },
  {
    id: "phoneNumber",
    label: "Phone Number",
    minWidth: "140px",
    required: true,
  },
  { id: "address", label: "Address", minWidth: "200px", required: false },
  {
    id: "totalAmount",
    label: "Total Amount",
    minWidth: "120px",
    required: true,
  },
  { id: "status", label: "Status", minWidth: "120px", required: true },
  { id: "action", label: "Action", minWidth: "120px", required: true },
];

export default function OrderTable({
  onSettingsClick,
  anchorEl,
  onCloseSettings,
  setRefetchFn,
  searchQuery,
  filters,
  visibleColumns,
  onColumnToggle,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isLoading, setIsLoading] = React.useState(false);
  const [filteredData, setFilteredData] = React.useState({
    orders: [],
    totalCount: 0,
  });

  // Use dummy data for filtering and display
  React.useEffect(() => {
    // Simulate loading
    setIsLoading(true);

    setTimeout(() => {
      let filtered = [...dummyOrders];

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          order =>
            order.firmName.toLowerCase().includes(query) ||
            order.phoneNumber.toLowerCase().includes(query) ||
            order.address.toLowerCase().includes(query),
        );
      }

      // Apply status filter
      if (filters.status) {
        filtered = filtered.filter(order => order.status === filters.status);
      }

      // Apply amount filter
      if (filters.amount) {
        const [min, max] = filters.amount.includes("+")
          ? [parseInt(filters.amount.replace("+", "")), Infinity]
          : filters.amount.split("-").map(v => parseInt(v));

        filtered = filtered.filter(
          order => order.totalAmount >= min && order.totalAmount <= max,
        );
      }

      // Apply sorting
      if (filters.sortBy) {
        const [field, direction] = filters.sortBy.split("_");

        filtered.sort((a, b) => {
          let valueA, valueB;

          if (field === "firmName") {
            valueA = a.firmName;
            valueB = b.firmName;
          } else if (field === "amount") {
            valueA = a.totalAmount;
            valueB = b.totalAmount;
          } else if (field === "date") {
            valueA = new Date(a.dateCreated);
            valueB = new Date(b.dateCreated);
          }

          if (direction === "asc") {
            return valueA > valueB ? 1 : -1;
          } else {
            return valueA < valueB ? 1 : -1;
          }
        });
      }

      setFilteredData({
        orders: filtered,
        totalCount: filtered.length,
      });
      setIsLoading(false);
    }, 800); // Simulate network delay
  }, [searchQuery, filters, page, rowsPerPage]);

  // Reset to first page when filters or search changes
  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, filters]);

  // For demo, simulate a refetch function
  React.useEffect(() => {
    if (typeof setRefetchFn === "function") {
      setRefetchFn(() => async () => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsLoading(false);
        return true;
      });
    }
  }, [setRefetchFn]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Filter visible columns
  const visibleColumnsList = defaultColumns.filter(
    column => visibleColumns[column.id],
  );

  // Format currency
  const formatCurrency = amount => {
    return `₹${amount.toLocaleString()}`;
  };

  // Transform data to table format
  const rows = React.useMemo(() => {
    const startIndex = page * rowsPerPage;
    const displayedOrders = filteredData.orders.slice(
      startIndex,
      startIndex + rowsPerPage,
    );

    return displayedOrders.map(order => ({
      _id: order._id,
      orderId: order._id.substring(3),
      firmName: order.firmName,
      date: order.date,
      phoneNumber: order.phoneNumber,
      address: order.address,
      totalAmount: formatCurrency(order.totalAmount),
      status: order.status,
    }));
  }, [filteredData, page, rowsPerPage]);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: "none",
        border: "1px solid #E5E7EB",
        borderRadius: "0.5rem",
      }}>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => onColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{ maxHeight: "calc(100vh - 300px)" }}
        className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumnsList.map(column => (
                <TableCell
                  key={column.id}
                  align="center"
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: "#F9FAFB",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#374151",
                    borderBottom: "2px solid #E5E7EB",
                  }}
                  className="font-outfit">
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="flex items-center justify-center py-4">
                    <CircularProgress size={40} />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnsList.length} align="center">
                  <div className="py-4 text-gray-500">No orders found</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map(row => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row._id}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                  }}>
                  {visibleColumnsList.map(column => {
                    const value = row[column.id];
                    return (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          color: "#4B5563",
                        }}
                        className="font-outfit">
                        {column.id === "status" ? (
                          <div
                            className={`px-3 py-1 rounded-full text-sm font-medium inline-block
                            ${
                              value === "Pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : value === "Approved"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}>
                            {value}
                          </div>
                        ) : column.id === "action" ? (
                          <div className="flex items-center justify-center">
                            <NavLink
                              to={`/orders/orderdetail/${row._id}`}
                              className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors">
                              <Eye size={16} className="text-[#5652B0]" />
                            </NavLink>
                          </div>
                        ) : (
                          value
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredData.totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: "1px solid #E5E7EB",
          ".MuiTablePagination-select": {
            borderRadius: "0.375rem",
          },
        }}
      />

      {isLoading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </Paper>
  );
}

export { defaultColumns };
